import React, { useEffect, useState, useRef } from 'react';
import { Award, Download, Loader2, Lock } from 'lucide-react';
import { portalApi, usePortalAuth } from '../../contexts/PortalAuthContext';

export default function PortalCertificate() {
  const { user } = usePortalAuth();
  const [cert, setCert] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const certRef = useRef();

  useEffect(() => {
    (async () => {
      try {
        const p = await portalApi.get('/progress');
        setProgress(p.data);
        if (p.data.completion_percent >= 100) {
          const c = await portalApi.get('/certificate');
          setCert(c.data);
        }
      } catch (e) {/* ignore */}
      finally { setLoading(false); }
    })();
  }, []);

  const handlePrint = () => {
    // Use the browser's native print dialog (user can choose "Save as PDF")
    window.print();
  };

  if (loading) return <div className="text-slate-400 text-sm">Loading…</div>;

  if (!cert) {
    const pct = progress?.completion_percent || 0;
    return (
      <div className="max-w-xl mx-auto text-center py-12" data-testid="portal-cert-locked">
        <div className="w-20 h-20 mx-auto rounded-full bg-slate-800 flex items-center justify-center mb-6">
          <Lock className="w-9 h-9 text-slate-500" />
        </div>
        <h1 className="text-2xl font-semibold text-white mb-2">Certificate locked</h1>
        <p className="text-slate-400 mb-6">
          Complete all modules to unlock your Certificate of Completion. You&apos;re {pct}% there.
        </p>
        <div className="h-2 bg-slate-800 rounded-full overflow-hidden max-w-xs mx-auto">
          <div className="h-full bg-gradient-to-r from-amber-500 to-orange-600" style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="portal-cert-issued">
      <header className="mb-6 flex items-center justify-between gap-4 flex-wrap print:hidden">
        <div>
          <div className="text-amber-400 text-xs uppercase tracking-widest mb-2">Achievement unlocked</div>
          <h1 className="text-3xl font-semibold text-white">Your Certificate</h1>
        </div>
        <button
          onClick={handlePrint}
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold rounded-lg px-4 py-2 text-sm"
          data-testid="cert-download-btn"
        >
          <Download className="w-4 h-4" /> Print / Save as PDF
        </button>
      </header>

      {/* The certificate itself */}
      <div
        ref={certRef}
        className="relative bg-gradient-to-br from-stone-50 to-amber-50 rounded-3xl border-8 border-double border-amber-700/40 p-8 sm:p-14 print:border-2 print:rounded-none print:shadow-none print:bg-white"
        style={{ fontFamily: 'Georgia, serif', aspectRatio: '1.414 / 1' }}
      >
        {/* Decorative corners */}
        <div className="absolute top-6 left-6 w-12 h-12 border-t-2 border-l-2 border-amber-700/60" />
        <div className="absolute top-6 right-6 w-12 h-12 border-t-2 border-r-2 border-amber-700/60" />
        <div className="absolute bottom-6 left-6 w-12 h-12 border-b-2 border-l-2 border-amber-700/60" />
        <div className="absolute bottom-6 right-6 w-12 h-12 border-b-2 border-r-2 border-amber-700/60" />

        <div className="flex flex-col items-center text-center h-full justify-between">
          <div>
            <Award className="w-12 h-12 sm:w-16 sm:h-16 text-amber-700 mx-auto mb-3" />
            <div className="text-xs sm:text-sm uppercase tracking-[0.3em] text-amber-800 mb-1">Audio &amp; Co</div>
            <h2 className="text-2xl sm:text-4xl font-bold text-stone-900 mb-1">Certificate of Completion</h2>
            <div className="text-xs sm:text-sm text-stone-600 italic">Live Training Programme</div>
          </div>

          <div className="my-6 sm:my-8">
            <div className="text-stone-500 text-sm mb-2">This is awarded to</div>
            <div className="text-3xl sm:text-5xl font-bold text-amber-900 mb-4" style={{ fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive' }}>
              {cert.user_name}
            </div>
            <p className="text-stone-700 max-w-xl mx-auto text-sm sm:text-base leading-relaxed">
              For successfully completing all {cert.modules_completed} modules of the Audio &amp; Co Live Training Programme,
              demonstrating commitment, creativity, and the practical skills required to build an AI-powered digital business.
            </p>
          </div>

          <div className="w-full flex items-end justify-between text-xs sm:text-sm text-stone-700">
            <div className="text-left">
              <div className="border-t border-stone-400 pt-1 mt-6 min-w-[160px]" style={{ fontFamily: '"Brush Script MT", "Lucida Handwriting", cursive', fontSize: '1.5rem', borderTop: 'none', paddingTop: 0 }}>
                Jo Day
              </div>
              <div className="border-t border-stone-400 text-stone-500 mt-1 pt-1 text-xs">Founder, Audio &amp; Co</div>
            </div>
            <div className="text-right text-xs">
              <div>Issued: {new Date(cert.issued_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
              <div className="text-stone-500">Certificate ID: {cert.cert_id}</div>
            </div>
          </div>
        </div>
      </div>

      <p className="text-xs text-slate-500 text-center mt-6 print:hidden">
        Tip: use your browser&apos;s &ldquo;Save as PDF&rdquo; option in the print dialog for a digital copy.
      </p>

      {/* Print styles to make sure only the cert prints */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          [data-testid="portal-cert-issued"], [data-testid="portal-cert-issued"] * { visibility: visible; }
          [data-testid="portal-cert-issued"] { position: absolute; inset: 0; padding: 0; margin: 0; }
        }
      `}</style>
    </div>
  );
}
